import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, importProvidersFrom, inject, LOCALE_ID, provideAppInitializer } from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MAT_DATE_LOCALE, DateAdapter as MaterialDateAdapter } from '@angular/material/core';
import { bootstrapApplication, BrowserModule, HAMMER_GESTURE_CONFIG, HammerModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';

import { MyHammerConfig } from './app/app.module';
import { TranslocoRootModule } from './app/transloco-root.module';

import { LibCoreModule } from '@lib-core/lib-core.module';
import { LoaderModule } from '@lib-shared/loader/loader.module';

import { LoggerService } from '@lib-shared/logger/services/logger.service';
import { provideAppLogger } from '@lib-shared/logger/providers/app-logger.provider';
import { AuthEventsLoggerService } from '@lib-core/auth/services/auth-events-logger.service';

import { AppComponent } from './app/app.component';

import { CMS_COMPONENT_CORE_CONFIG } from '@cms/components/core';
import { CMS_EVENT_CONFIG } from '@cms/components/event';
import { CMS_OFFER_CONFIG } from '@cms/components/offer';
import { cmsCoreEditorProviders } from '@cms/core/editor';
import { cmsCoreSeoProviders } from '@cms/core/seo';
import { cmsCoreConfigDefault, cmsCoreSharedProviders } from '@cms/core/shared';
import { CMS_IMAGE_CONFIG, cmsCoreImageProviders } from '@cms/core/image';
import { CMS_I18N_CONFIG } from '@cms/core/i18n';
import { CMS_CORE_CONFIG } from '@cms/core/shared';
import { environment } from '@environments/environment';
import { langProvider } from '@lib-shared/lang/providers/lang.provider';
import { DATE_HELPER_CONFIG, dateHelperConfigDefault } from '@mill/helpers/date';
import { SCREEN_HELPER_CONFIG, screenHelperConfigDefault } from '@mill/helpers/screen';
import { ACCOUNT_CONFIG, accountConfig } from '@mymill/core/account';
import { ARTICLES_ENDPOINTS_CONFIG, articlesConfig } from '@mymill/core/articles';
import { AUTH_ENDPOINTS_CONFIG, authConfig } from '@mymill/core/auth';
import { CMS_MYMILL_CONFIG } from '@mymill/core/cms';
import { stepperConfig } from '@mymill/core/stepper';
import {
  PAYMENT_ENDPOINTS_CONFIG,
  paymentConfig,
  TRANSACTION_ENDPOINTS_CONFIG,
  transactionConfig,
} from '@mymill/core/transaction';
import { MESSAGES_ENDPOINTS_CONFIG } from '@mymill/core/messagerie';
import { DateAdapter as CalendarDateAdapter, CalendarModule } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/moment';
import moment from 'moment';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { SHOP_ARTICLE_CONFIG, SHOP_PAYMENT_CONFIG, SHOP_TRANSACTION_CONFIG } from '@mill/core/shop';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { routes } from './app/app.routes';

if (environment.production) {
  enableProdMode();
}
export const momentAdapterFactory = (): CalendarDateAdapter => adapterFactory(moment);

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      LoaderModule,
      // angular modules
      BrowserModule,
      HammerModule,
      // project core
      LibCoreModule,
      // ga
      NgxGoogleAnalyticsModule.forRoot(environment.gtag),
      NgxGoogleAnalyticsRouterModule,
      TranslocoRootModule
    ),
    {
      provide: MaterialDateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    importProvidersFrom(
      CalendarModule.forRoot({
        provide: CalendarDateAdapter,
        useFactory: momentAdapterFactory,
      })
    ),
    //...cmsCoreProviders => REMOVED TO FIX cmsCoreI18nProvider problem with the interceptor,
    ...cmsCoreEditorProviders,
    ...cmsCoreSharedProviders,
    ...cmsCoreImageProviders,
    ...cmsCoreSeoProviders,
    {
      provide: DATE_HELPER_CONFIG,
      useValue: dateHelperConfigDefault,
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: MyHammerConfig,
    },
    {
      provide: CMS_CORE_CONFIG,
      useValue: {
        ...cmsCoreConfigDefault,
        api: environment.api.backend.url + '/api/cms',
      },
    },
    {
      provide: LOCALE_ID,
      useValue: 'fr',
    },
    {
      provide: CMS_I18N_CONFIG,
      useValue: {
        langs: [
          {
            key: 'fr',
            code: 'fr',
          },
        ],
      },
    },
    {
      provide: DATE_HELPER_CONFIG,
      useValue: dateHelperConfigDefault,
    },
    {
      provide: SCREEN_HELPER_CONFIG,
      useValue: screenHelperConfigDefault,
    },
    {
      provide: CMS_OFFER_CONFIG,
      useValue: { endpointUrl: environment.api.offers.signupUrl },
    },
    {
      provide: CMS_EVENT_CONFIG,
      useValue: {
        endpointUrl: environment.api.events.signupUrl,
        siteCode: 'mymillennium',
        guestsLimit: {
          elite: 1,
          platinum: 5,
          diamond: 10,
        },
      },
    },
    {
      provide: CMS_IMAGE_CONFIG,
      useValue: {
        presetTransformation: {
          lowQualityKey: 'webp-quality-1',
          highQualityKey: 'webp-quality-75',
        },
      },
    },
    {
      provide: CMS_MYMILL_CONFIG,
      useValue: {
        endpointBase: environment.api.backend.url + '/api/cms',
      },
    },
    {
      provide: ARTICLES_ENDPOINTS_CONFIG,
      useValue: {
        endpointPostArticlesPrices: environment.api.backend.url + '/api/articles/prices',
        defaultImage: environment.api.cms.url + '/assets/' + environment.shared.article.defaultImage,
      },
    },
    ...authConfig.providers,
    {
      provide: AUTH_ENDPOINTS_CONFIG,
      useValue: {
        autoLoginEndpoint: environment.api.backend.url + '/api/auth/auto-login',
        loginEndpoint: environment.api.backend.url + '/api/auth',
        refreshTokenEndpoint: environment.api.backend.url + '/api/auth/refresh',
        selfResetEndpoint: environment.api.backend.url + '/api/users/self-reset',
        selfResetLoginEndpoint: environment.api.backend.url + '/api/users/self-reset/login',
        loadProfilEndpoint: environment.api.backend.url + '/api/users/me',
      },
    },
    ...paymentConfig.providers,
    {
      provide: PAYMENT_ENDPOINTS_CONFIG,
      useValue: {
        endpointGetAliases: environment.api.backend.url + '/api/payments/aliases/me',
      },
    },
    ...transactionConfig.providers,
    {
      provide: TRANSACTION_ENDPOINTS_CONFIG,
      useValue: {
        endpointPutPaymentCheck: environment.api.backend.url + '/api/transactions/payment/check',
        endpointPostCompute: environment.api.backend.url + '/api/transactions/compute',
        endpointPutInitiatePayment: environment.api.backend.url + '/api/transactions/payment/initiate',
        endpointPutFinalizePayment: environment.api.backend.url + '/api/transactions/payment/finalize',
        endpointGetDTSettings: environment.api.backend.url + '/api/externals/datatrans/settings',
        endpointGetCanInvoiceCompany: {
          firstPart: environment.api.backend.url + '/api/payments/',
          lastPart: '/can-invoice-company',
        },
        endpointPutSaveAdmin: environment.api.backend.url + '/api/transactions/admin',
      },
    },
    {
      provide: MESSAGES_ENDPOINTS_CONFIG,
      useValue: {
        endpointGetMessages: environment.api.backend.url + '/api/conversation/messages',
        endpointPostMessage: environment.api.backend.url + '/api/conversation/message',
      },
    },
    ...articlesConfig.providers,
    ...accountConfig.providers,
    {
      provide: ACCOUNT_CONFIG,
      useValue: {
        endpointGetTcposBalance: environment.api.backend.url + '/api/users/me/balances/prepaids/tcpos',
      },
    },
    {
      provide: CMS_COMPONENT_CORE_CONFIG,
      useValue: {
        prefixWithLang: false,
      },
    },
    // @mill/core/shop
    {
      provide: SHOP_ARTICLE_CONFIG,
      useValue: {
        api: {
          load: environment.api.backend.url + '/api/articles/prices',
        },
      },
    },
    {
      provide: SHOP_TRANSACTION_CONFIG,
      useValue: {
        api: {
          load: environment.api.backend.url + '/api/transactions/all',
          compute: environment.api.backend.url + '/api/transactions/compute',
          payment: {
            initiate: environment.api.backend.url + '/api/transactions/payment/initiate',
            finalize: environment.api.backend.url + '/api/transactions/payment/finalize',
            check: environment.api.backend.url + '/api/transactions/payment/check',
          },
        },
      },
    },
    {
      provide: SHOP_PAYMENT_CONFIG,
      useValue: {},
    },

    ...stepperConfig.providers,
    langProvider,
    provideAppLogger(environment.logger),
    provideAppInitializer(() => {
      inject(AuthEventsLoggerService).init();
    }),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      })
    ),
  ],
}).catch((err) => {
  LoggerService.error(err);
});
